/**
 * Tenant-facing alerts (PLAN §11.1, D27; phase 7b item 4): the alerts a tenant
 * has now, their history, and who is told — the notification settings and
 * channels (email to users and groups, a Slack incoming webhook, a generic
 * HMAC-signed webhook).
 *
 * The alert types, severities and the rules' semantics are the pure core's
 * (`@norna/tenant-alerts`); this module is their wire shape. Cloud only: a node
 * has no alerts of its own.
 *
 * **Secrets.** A Slack hook URL IS a credential (whoever holds it posts to the
 * channel), and so is a webhook's signing secret. Both are written once and
 * stored in Secrets Manager under the tenant's path; no answer ever carries
 * them — a channel shows `target`, a hint (the host, and a Slack hook's last
 * four characters), and `hasSecret`.
 */
import { ulidSchema } from '@norna/domain/primitives.js';
import { ALERT_TYPES, SEVERITIES } from '@norna/tenant-alerts/catalog.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { entityIdWireSchema, isoTimestampWireSchema, siteIdWireSchema, studioIdWireSchema, userIdWireSchema } from './primitives.js';

const groupIdSchema = entityIdWireSchema;

/** Closed: what a request names (a filter, a test, a rule's key) — the cloud validates what it accepts. */
export const alertTypeWireSchema = z.enum(ALERT_TYPES);
export type AlertTypeWire = z.infer<typeof alertTypeWireSchema>;
/** Open (D37 §4.3): an alert's type as a reader receives it — the catalog grows with every condition. */
const alertTypeOpenWireSchema = openEnum(ALERT_TYPES);
export type AlertTypeOpenWire = z.infer<typeof alertTypeOpenWireSchema>;
/** Closed: a severity a request sets (a rule, an override, a channel's minimum). */
export const severityWireSchema = z.enum(SEVERITIES);
/**
 * Open: a severity as a reader receives it — an alert, an alarm, and the settings' rules, overrides and channels. An
 * editor holding an `unknown` one leaves it out of what it saves (the cloud keeps it).
 */
export const severityOpenWireSchema = openEnum(SEVERITIES);
export type SeverityOpenWire = z.infer<typeof severityOpenWireSchema>;

/** An alert's identity: `<type>:<subject ids>` (`cert-expiring:<site>/<node>`, `device-disconnected-on-air:<production>/<device>`). */
const alertKeyWireSchema = z.string().regex(/^[a-z-]{1,40}:[A-Za-z0-9_.:/-]{1,300}$/, 'an alert key: `<type>:<ids>`');

const detailSchema = z.record(z.string().max(64), z.union([z.string().max(2000), z.number(), z.null()]));

export const alertWireSchema = z
  .object({
    key: alertKeyWireSchema,
    type: alertTypeOpenWireSchema,
    severity: severityOpenWireSchema,
    /** `active` while a condition holds; an event, and a condition that stopped, are `resolved`. */
    status: openEnum(['active', 'resolved']),
    siteId: siteIdWireSchema,
    studioId: studioIdWireSchema.nullable(),
    subject: z.string().max(500),
    summary: z.string().max(1000),
    detail: detailSchema,
    raisedAt: isoTimestampWireSchema,
    resolvedAt: isoTimestampWireSchema.nullable(),
    /** How many times it was raised (a condition's episodes, an event's occurrences). */
    occurrences: z.number().int().positive(),
    /** When people were last notified about it; null if never. */
    notifiedAt: isoTimestampWireSchema.nullable(),
    /**
     * Acknowledged for everyone in this episode (`POST /alerts/acknowledge`): who and when. Null while nobody has —
     * and again once it resolves and is raised anew (an acknowledgement covers one episode).
     */
    acknowledged: z.object({ at: isoTimestampWireSchema, by: z.string().max(320) }).strict().nullable(),
  })
  .strict();
export type AlertWire = z.infer<typeof alertWireSchema>;

export const listAlertsQueryWireSchema = z
  .object({
    status: z.enum(['active', 'all']).optional().describe('`active` (the default): the conditions that hold now. `all`: every alert the tenant has a state for, the recent events included.'),
  })
  .strict();

const alertCountsWireSchema = z.object({ info: z.number().int().nonnegative(), warning: z.number().int().nonnegative(), critical: z.number().int().nonnegative() }).strict();
export type AlertCountsWire = z.infer<typeof alertCountsWireSchema>;

export const listAlertsResponseWireSchema = z
  .object({
    alerts: z.array(alertWireSchema),
    /** The ACTIVE alerts the caller may see, per severity (the top bar's badge). */
    counts: alertCountsWireSchema,
  })
  .strict();

const alertTransitionWireSchema = openEnum(['raised', 'resolved', 'occurred', 'acknowledged']);

export const alertHistoryEntryWireSchema = z
  .object({
    entryId: ulidSchema,
    key: alertKeyWireSchema,
    type: alertTypeOpenWireSchema,
    transition: alertTransitionWireSchema,
    severity: severityOpenWireSchema,
    siteId: siteIdWireSchema,
    studioId: studioIdWireSchema.nullable(),
    subject: z.string().max(500),
    summary: z.string().max(1000),
    at: isoTimestampWireSchema,
    /** The channels it was sent to; empty when nobody was notified (the cooldown, the type off, no channel). */
    channelIds: z.array(entityIdWireSchema),
    /** Who did it: the acknowledging user of an `acknowledged` entry (the audit of it); null for what the cloud observed. */
    by: z.string().max(320).nullable(),
  })
  .strict();
export type AlertHistoryEntryWire = z.infer<typeof alertHistoryEntryWireSchema>;

const MAX_HISTORY_PAGE = 200;

export const listAlertHistoryQueryWireSchema = z
  .object({
    cursor: z.string().max(512).optional().describe('`nextCursor` of the previous page.'),
    limit: z.coerce.number().int().min(1).max(MAX_HISTORY_PAGE).optional().describe(`At most this many entries (default 50, at most ${String(MAX_HISTORY_PAGE)}).`),
    type: alertTypeWireSchema.optional().describe('Only entries of this alert type.'),
  })
  .strict();

export const listAlertHistoryResponseWireSchema = z.object({ entries: z.array(alertHistoryEntryWireSchema), nextCursor: z.string().nullable() }).strict();

/** `POST /alerts/acknowledge`: the alert to acknowledge for everyone. */
export const acknowledgeAlertRequestWireSchema = z.object({ key: alertKeyWireSchema.describe('The alert’s key (`listAlerts`).') }).strict();
export const alertResponseWireSchema = z.object({ alert: alertWireSchema }).strict();

// ─── settings ───

export const alertRuleWireSchema = z.object({ enabled: z.boolean(), severity: severityWireSchema }).strict();
export type AlertRuleWire = z.infer<typeof alertRuleWireSchema>;
/**
 * One rule per alert type — a PARTIAL record (the 2026-09-27 review §1.6). zod 4 makes an enum-keyed `z.record`
 * exhaustive, so when a new alert type shipped every older SPA's whole-record `PUT /alerts/settings` (which lacks
 * the new key) would have been refused, and a reader that dropped the key would have erased the new rule. Now a
 * type a request omits keeps its stored rule — it is one the client does not know (`mergeAlertSettings`,
 * `apps/cloud-api/src/alerts/settings.ts`) — and the cloud answers every type it knows, a missing one at its default.
 */
const alertRulesWireSchema = z.partialRecord(alertTypeWireSchema, alertRuleWireSchema);
const ruleOverrideWireSchema = z.object({ enabled: z.boolean().optional(), severity: severityWireSchema.optional() }).strict();
export const siteOverrideWireSchema = z.object({ siteId: siteIdWireSchema, rules: z.partialRecord(alertTypeWireSchema, ruleOverrideWireSchema) }).strict();
export type SiteOverrideWire = z.infer<typeof siteOverrideWireSchema>;

/**
 * The rules and overrides as the settings ANSWER them: the severity open, so a severity a newer cloud adds reads as
 * `unknown` rather than failing the whole document. An editor must not send an `unknown` back — it leaves that type
 * out of its save, and the cloud keeps the type's rule and overrides (`mergeAlertSettings`).
 */
const alertRuleOpenWireSchema = z.object({ enabled: z.boolean(), severity: severityOpenWireSchema }).strict();
const alertRulesOpenWireSchema = z.partialRecord(alertTypeWireSchema, alertRuleOpenWireSchema);
const ruleOverrideOpenWireSchema = z.object({ enabled: z.boolean().optional(), severity: severityOpenWireSchema.optional() }).strict();
const siteOverrideOpenWireSchema = z.object({ siteId: siteIdWireSchema, rules: z.partialRecord(alertTypeWireSchema, ruleOverrideOpenWireSchema) }).strict();

export const MAX_CHANNELS = 20;
const MAX_RECIPIENTS = 50;

/** Closed: the kind a new channel is created as. */
export const channelKindWireSchema = z.enum(['email', 'slack', 'webhook']);
export type ChannelKindWire = z.infer<typeof channelKindWireSchema>;
/** Open: a channel's kind as a reader lists it (a PATCH never sends the kind back, so nothing round-trips it). */
const channelKindOpenWireSchema = openEnum(['email', 'slack', 'webhook']);

const recipientsWireSchema = z
  .object({ userIds: z.array(userIdWireSchema).max(MAX_RECIPIENTS), groupIds: z.array(groupIdSchema).max(MAX_RECIPIENTS) })
  .strict()
  .refine((r) => r.userIds.length + r.groupIds.length >= 1, { message: 'name at least one user or group' });

export const alertChannelWireSchema = z
  .object({
    channelId: entityIdWireSchema,
    name: z.string().min(1).max(100),
    kind: channelKindOpenWireSchema,
    enabled: z.boolean(),
    /** The lowest severity it is sent (open: a listing; a PATCH sends it only when changed). */
    minSeverity: severityOpenWireSchema,
    /** The sites whose alerts it gets; null: every site. */
    siteIds: z.array(siteIdWireSchema).nullable(),
    /** Email only: who gets it. */
    recipients: z.object({ userIds: z.array(userIdWireSchema), groupIds: z.array(groupIdSchema) }).strict().nullable(),
    /** Where it goes, as a hint (never the URL itself): `hooks.slack.com …Ab3x`, `alerts.example.com`. Null for email. */
    target: z.string().max(200).nullable(),
    /** Slack and webhook: whether its URL (and signing secret) is stored. */
    hasSecret: z.boolean(),
    createdAt: isoTimestampWireSchema,
    updatedAt: isoTimestampWireSchema,
  })
  .strict();
export type AlertChannelWire = z.infer<typeof alertChannelWireSchema>;

export const alertSettingsWireSchema = z
  .object({
    /** Optimistic concurrency for `PUT /alerts/settings` (0 before the first save). */
    version: z.number().int().nonnegative(),
    /** The shortest interval between two notifications of one alert. */
    cooldownMinutes: z.number().int().min(1).max(1440),
    rules: alertRulesOpenWireSchema,
    siteOverrides: z.array(siteOverrideOpenWireSchema).max(200),
    channels: z.array(alertChannelWireSchema).max(MAX_CHANNELS),
    updatedAt: isoTimestampWireSchema.nullable(),
    updatedBy: z.string().max(320).nullable(),
  })
  .strict();
export type AlertSettingsWire = z.infer<typeof alertSettingsWireSchema>;

export const alertSettingsResponseWireSchema = z.object({ settings: alertSettingsWireSchema }).strict();

export const updateAlertSettingsRequestWireSchema = z
  .object({
    version: z.number().int().nonnegative().describe('The version this edit started from; another save since is `conflict`.'),
    cooldownMinutes: z.number().int().min(1).max(1440),
    rules: alertRulesWireSchema,
    siteOverrides: z.array(siteOverrideWireSchema).max(200),
  })
  .strict();
export type UpdateAlertSettingsRequestWire = z.infer<typeof updateAlertSettingsRequestWireSchema>;

/**
 * The host of an https URL (lowercased, port dropped), or null when it is not one this contract accepts: userinfo
 * (`user:pass@`) or an IPv6 literal. Parsed by pattern — the contract has no host APIs (no `URL` global here).
 */
export function channelUrlHost(raw: string): string | null {
  const match = /^https:\/\/([^/?#]+)(?:[/?#]|$)/i.exec(raw);
  const authority = match?.[1];
  if (authority === undefined || authority.includes('@') || authority.startsWith('[')) return null;
  const host = authority.replace(/:\d{1,5}$/, '').toLowerCase();
  return /^[a-z0-9.-]+$/.test(host) ? host : null;
}

/**
 * A URL a channel may post to (the cloud posts to it, so it must not aim inward): https, a public-looking host
 * NAME — no IP literal, no `localhost`, `.local` or `.internal`, at least one dot — and no credentials in it.
 */
export const channelUrlWireSchema = z
  .url({ protocol: /^https$/ })
  .max(2000)
  .refine(
    (raw) => {
      const host = channelUrlHost(raw);
      if (host === null) return false;
      const ipLiteral = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
      return !ipLiteral && host !== 'localhost' && !host.endsWith('.localhost') && !host.endsWith('.local') && !host.endsWith('.internal') && host.includes('.') && !host.endsWith('.');
    },
    { message: 'an https URL to a public host name (no IP address, localhost or credentials)' },
  );

export const SLACK_HOOK_PREFIX = 'https://hooks.slack.com/';
export const MIN_SIGNING_SECRET_CHARS = 16;

export const createAlertChannelRequestWireSchema = z
  .object({
    channelId: entityIdWireSchema.optional().describe('Makes a retry idempotent: a channel with this id is returned as it is.'),
    name: z.string().min(1).max(100),
    kind: channelKindWireSchema,
    enabled: z.boolean().optional(),
    minSeverity: severityWireSchema,
    siteIds: z.array(siteIdWireSchema).max(200).nullable(),
    recipients: recipientsWireSchema.nullable().optional(),
    url: channelUrlWireSchema.nullable().optional(),
    signingSecret: z.string().min(MIN_SIGNING_SECRET_CHARS).max(256).nullable().optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    for (const [path, message] of channelProblems(body)) ctx.addIssue({ code: 'custom', path: [path], message });
  });
export type CreateAlertChannelRequestWire = z.infer<typeof createAlertChannelRequestWireSchema>;

type ChannelFields = { kind: ChannelKindWire; recipients?: unknown; url?: string | null | undefined; signingSecret?: string | null | undefined };

function isSet<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/** What a new channel of its kind lacks or must not have: `[field, message]` pairs. */
function channelProblems(body: ChannelFields): [string, string][] {
  const problems: [string, string][] = [];
  if (body.kind === 'email') {
    if (!isSet(body.recipients)) problems.push(['recipients', 'an email channel names its recipients']);
    if (isSet(body.url)) problems.push(['url', 'an email channel has no URL']);
    return problems;
  }
  if (!isSet(body.url)) problems.push(['url', `a ${body.kind} channel needs its URL`]);
  else if (body.kind === 'slack' && !body.url.startsWith(SLACK_HOOK_PREFIX)) problems.push(['url', `a Slack incoming webhook URL starts ${SLACK_HOOK_PREFIX}`]);
  if (body.kind === 'webhook' && !isSet(body.signingSecret)) problems.push(['signingSecret', 'a webhook channel needs a signing secret (the receiver verifies the HMAC with it)']);
  if (body.kind === 'slack' && isSet(body.signingSecret)) problems.push(['signingSecret', 'a Slack channel is not signed']);
  if (isSet(body.recipients)) problems.push(['recipients', 'only an email channel has recipients']);
  return problems;
}

/** What a PATCH may change; the kind is fixed. A new `url` (or `signingSecret`) replaces the stored one. */
export const updateAlertChannelRequestWireSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    enabled: z.boolean().optional(),
    minSeverity: severityWireSchema.optional(),
    siteIds: z.array(siteIdWireSchema).max(200).nullable().optional(),
    recipients: recipientsWireSchema.optional(),
    url: channelUrlWireSchema.optional(),
    signingSecret: z.string().min(MIN_SIGNING_SECRET_CHARS).max(256).optional(),
  })
  .strict();
export type UpdateAlertChannelRequestWire = z.infer<typeof updateAlertChannelRequestWireSchema>;

export const alertChannelPathWireSchema = z.object({ channelId: entityIdWireSchema.describe('The channel’s id.') }).strict();
export const alertChannelResponseWireSchema = z.object({ channel: alertChannelWireSchema }).strict();

export const testAlertRequestWireSchema = z
  .object({
    channelId: entityIdWireSchema.nullable().describe('The channel to test; null tests every enabled channel.'),
    type: alertTypeWireSchema.optional().describe('The alert type the test message pretends to be (default `failover`).'),
  })
  .strict();

const testAlertResultWireSchema = z.object({ channelId: entityIdWireSchema, ok: z.boolean(), detail: z.string().max(500) }).strict();
export const testAlertResponseWireSchema = z.object({ results: z.array(testAlertResultWireSchema) }).strict();
export type TestAlertResultWire = z.infer<typeof testAlertResultWireSchema>;
